export interface CompatibilityEntry {
  type: string;
  name: string;
  description: string;
}

export interface JiraiResultType {
  code: string;
  name: string;
  tagline: string;
  description: string[];
  strengths: string[];
  weaknesses: string[];
  goodMatch: string;
  badMatch: string;
  accentColor: string;
  imageUrl?: string;
  compatibility?: {
    friendGood: CompatibilityEntry;
    friendBad: CompatibilityEntry;
    loverGood: CompatibilityEntry;
    loverBad: CompatibilityEntry;
  };
}

export const jiraiResultTypes: Record<string, JiraiResultType> = {
  DEOB: {
    code: 'DEOB',
    name: 'メンヘラ核弾頭',
    tagline: '愛してるなら証明しろよ、今すぐ全部',
    description: [
      '「愛されていない」「雑に扱われた」と感じた瞬間に安全装置が外れ、周囲を巻き込んで大爆発する歩く核弾頭です。怒りや悲しみが限界を超えると、大声で泣き叫んだり、画面が埋まるほどの長文LINEを連投したりと、感情の赴くままに破壊の限りを尽くします。',
      'しかし、その激しい怒りの根源にあるのは「私だけを見てほしい」「見捨てないでほしい」という重すぎる愛情。相手を攻撃しているように見えて、実は自分自身が一番傷つき、パニックになっているだけなのです。',
      '爆発した後は急にケロッとしたり「ごめんね」と甘えたりするため、相手はジェットコースターのような疲労感を抱えますが、その激しさゆえに謎の依存関係を生み出しやすいのも特徴です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '情緒ブラックボックス（DEOQ）',
    badMatch: '冷酷リセットボタン（ALOB）',
    accentColor: '#e11d48',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/b9555a27-ab89-44d9-90d5-4e6499c076c0_DEOB.png',
    compatibility: {
      friendGood: { type: 'AEOB', name: '瞬間湯沸かし器', description: 'どちらも爆発型で、ちょっとしたことで大炎上する友人関係。喧嘩は激しいが仲直りも早く、嵐のような友情が続く。' },
      friendBad: { type: 'ALOQ', name: '完全シャットダウン', description: '核弾頭が騒ぐたびにシャットダウンが減点し、ある日突然全ブロック。核弾頭はなぜ消えたか理解できず、混乱と傷心だけが残る最悪の組み合わせ。' },
      loverGood: { type: 'DLSQ', name: 'メンタル監禁官', description: '核弾頭が「返事して！」と騒ぐほど、監禁官は無言の重い圧をかけてくる。核弾頭は息苦しさを感じながらも離れられず、監禁官は満足して見守る共依存の完成形。' },
      loverBad: { type: 'ALOQ', name: '完全シャットダウン', description: '核弾頭が騒ぐたびにシャットダウンが減点し、ある日突然全ブロック。核弾頭はなぜ消えたか理解できず、混乱と傷心だけが残る。' },
    },
  },
  DEOQ: {
    code: 'DEOQ',
    name: '情緒ブラックボックス',
    tagline: '全部わかってほしいのに、何も言わない',
    description: [
      '怒りや不満が溜まっても直接言葉には出さず、不機嫌なオーラやため息で相手に「察しろ」と要求する高度な地雷です。急にLINEを既読無視したり、極端に冷たい態度をとるなどの「無言の制裁」を駆使し、相手が焦ってご機嫌取りをしてくるのを密室でじっと待っています。',
      '根底にあるのは「本当に私のことが好きなら、言わなくてもわかるはず」という強烈な依存心。相手の愛情を測るために、あえて自分から壁を作って試すような行動を繰り返します。',
      '本人は無言で相手をコントロールしているつもりですが、相手からすれば「何に怒っているのか全く読めない恐怖の箱」であり、徐々にメンタルを削られた相手が静かに離脱していくリスクを抱えています。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: 'メンヘラ核弾頭（DEOB）',
    badMatch: '感情オフライン人間（ALSQ）',
    accentColor: '#7c3aed',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/ebd5d14a-4666-4388-b550-72e407e313eb_DEOQ.png',
    compatibility: {
      friendGood: { type: 'AESQ', name: 'ひとりで沈む人', description: 'どちらも干渉しない静かな関係で、一定の距離感が保たれる。深い絆は生まれにくいが、お互いに「ちょうどいい距離」を保てる稀な相手。' },
      friendBad: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発しても、ブラックボックスは既読無視でスルー。湯沸かし器は「なんで反応しないんだ！」とさらにキレ、ブラックボックスは冷たく距離を置く。' },
      loverGood: { type: 'DLOB', name: '論破モンスター彼女', description: 'ブラックボックスの無言に対し、論破モンスターが「何が不満か言え」と証拠を並べて追い詰める。依存心の強さが釣り合っており、ぶつかりながらも関係が深まる。' },
      loverBad: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発しても、ブラックボックスは既読無視でスルー。湯沸かし器は「なんで反応しないんだ！」とさらにキレ、ブラックボックスは冷たく距離を置く。' },
    },
  },
  DESB: {
    code: 'DESB',
    name: '涙の無限ループ',
    tagline: 'ごめんね…でも止まらないの',
    description: [
      'トラブルが起きたとき、相手を責めるのではなく「どうせ私が全部悪いんだよね」と盛大に自分を責め立てることで、相手の逃げ道を塞ぐタイプです。人前でも平気で大号泣し、圧倒的な自己犠牲と悲劇のヒロインっぷりを見せつけることで、相手に特大の罪悪感を植え付けます。',
      '「こんなに可哀想な私を置いていくなんて、あなたは最低な人間だ」というメッセージを涙で包んで突きつけるため、相手は反論すらできず、ただ謝って慰めるしかなくなります。',
      '一見弱々しく見えますが、実は「自分の弱さや涙」を最強の武器として使いこなし、相手を精神的に縛り付けてコントロールする、ある意味で最も生存能力の高いメンヘラと言えるでしょう。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '察してちゃんヒロイン（DESQ）',
    badMatch: '瞬間湯沸かし器（AEOB）',
    accentColor: '#db2777',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/b33d14c5-9d8c-4dfa-86f7-324956ee1d43_DESB.png',
    compatibility: {
      friendGood: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発すると、涙ループが「私のせいで怒らせちゃった…」と泣き崩れる。湯沸かし器は罪悪感で戸惑い、暴れる気力を失う稀なパターン。' },
      friendBad: { type: 'ALSQ', name: '感情オフライン人間', description: '涙ループがどれだけ泣いても、オフライン人間は「あっそ」で完結。涙ループの最強武器である涙が全く効かない、天敵のような相手。' },
      loverGood: { type: 'DLSQ', name: 'メンタル監禁官', description: '涙ループが泣き崩れると、監禁官は無言で重い視線を送るだけ。涙ループは「何も言ってくれない」と余計に泣き、監禁官はますます無言の圧を強める。歪んでいるが強烈に縛り合う関係。' },
      loverBad: { type: 'AESB', name: '情緒クラッシュ逃亡者', description: '涙ループが泣き崩れると、逃亡者がパニックになって泣きながら逃走。慰める側と逃げる側が毎回入れ替わり、どちらも消耗するだけで関係が前に進まない。' },
    },
  },
  DESQ: {
    code: 'DESQ',
    name: '察してちゃんヒロイン',
    tagline: '察してくれないなら、もういいよ…',
    description: [
      '傷ついたりムカついたりしても直接は怒らず、誰にも言わずに勝手に病んでいく完全内向型の地雷です。相手に文句を言う代わりに、SNSの裏垢で病みポエムを連投したり、「もう疲れた」と意味深な匂わせ投稿をして、誰かが心配して探しに来てくれるのをひたすら待っています。',
      '根底には「見捨てられたくない」という強い依存があるものの、自分から「助けて」と言うプライドは許さないため、常に受け身で相手の行動を求めます。',
      '密室で一人で絶望を深めていくため、相手が気づいた時にはすでに手遅れレベルまで病状が悪化していることも。重すぎる愛情を無言で抱え込む、取り扱い難易度MAXの引きこもり型地雷です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '涙の無限ループ（DESB）',
    badMatch: '冷酷リセットボタン（ALOB）',
    accentColor: '#9333ea',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/22cc7a6d-d239-444f-aec6-78eb7ef28867_DESQ.png',
    compatibility: {
      friendGood: { type: 'AESQ', name: 'ひとりで沈む人', description: 'どちらも静かに引きこもるタイプで、干渉し合わない穏やかな関係。お互いに「なんとなく知ってる人」で終わりやすいが、それが逆に心地よい。' },
      friendBad: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発すると、察してちゃんは「やっぱり私のせいだ」と病んでSNSに消えていく。湯沸かし器は「なんで逃げるんだ！」とさらに爆発する。' },
      loverGood: { type: 'DLOQ', name: '静かなる詰将棋', description: '察してちゃんが病みポエムを投稿していると、詰将棋が密かに観察して証拠を収集。察してちゃんは「気にかけてくれている」と感じるが、実は制裁の準備をされている危うい関係。' },
      loverBad: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発すると、察してちゃんは「やっぱり私のせいだ」と病んでSNSに消えていく。湯沸かし器は「なんで逃げるんだ！」とさらに爆発する。' },
    },
  },
  DLOB: {
    code: 'DLOB',
    name: '論破モンスター彼女',
    tagline: 'はいこれ証拠。逃げ道あると思った？',
    description: [
      '愛されたいという強い依存心を抱えながらも、素直に甘えることができず、相手の矛盾やミスを理詰めでボコボコにする戦闘狂タイプです。浮気や裏切りの気配を感じると、感情的に泣き喚くのではなく、LINEのスクショや矛盾点などの「証拠」を完璧に揃えた上で相手を追い詰めます。',
      '「あの時のあの発言と辻褄が合わないよね？」「どういう論理でそうなるの？」と、逃げ道を完全に塞いでから着火するため、相手はぐうの音も出ません。',
      '本当はただ安心したい、愛されたいだけなのに、正論というナイフで相手を刺し殺してしまうため、論破すればするほど相手の心が離れていくという悲しきジレンマを抱えたサイコパス系ヒロインです。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '静かなる詰将棋（DLOQ）',
    badMatch: 'メンヘラ核弾頭（DEOB）',
    accentColor: '#0891b2',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/f193e863-2801-42a6-8301-ad4a19f0483c_DLOB.png',
    compatibility: {
      friendGood: { type: 'DEOQ', name: '情緒ブラックボックス', description: 'ブラックボックスの無言に対し、論破モンスターが「何が不満か言え」と証拠を並べて追い詰める。ブラックボックスはさらに黙り込み平行線だが、不思議と関係が続く。' },
      friendBad: { type: 'ALSQ', name: '感情オフライン人間', description: '論破モンスターが証拠を並べても、オフライン人間は「あっそ」で終了。論破モンスターの完璧な論理が完全に無視され、存在意義を失う。' },
      loverGood: { type: 'DEOQ', name: '情緒ブラックボックス', description: 'ブラックボックスが「察して」と無言で圧をかけると、論破モンスターが「何が不満か言え」と迫る。お互いの隠した感情を引き出し合い、依存心の強さが釣り合っているのも安定要因。' },
      loverBad: { type: 'ALSQ', name: '感情オフライン人間', description: '論破モンスターが証拠を並べても、オフライン人間は「あっそ」で終了。論破モンスターの完璧な論理が完全無視され、存在意義を失う。' },
    },
  },
  DLOQ: {
    code: 'DLOQ',
    name: '静かなる詰将棋',
    tagline: '気づいた時にはもう詰んでるよ',
    description: [
      '相手の裏切りや不満に気づいても、その場では決してキレません。怒りを冷凍保存し、水面下で証拠を完璧に集め、相手が一番ダメージを受ける絶好のタイミングで無言の制裁を下す、恐るべき完全犯罪者タイプです。',
      '相手に依存し、執着しているからこそ「絶対に逃がさない」という執念が深く、感情に任せて暴れるような隙は見せません。そしてある日突然、言い訳の余地もない証拠を突きつけるか、あるいは理由も告げずに全ての連絡先をブロックして姿を消します。',
      '相手に「なぜ切られたのか」という強烈なトラウマだけを残す、最も冷酷な地雷です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '論破モンスター彼女（DLOB）',
    badMatch: '瞬間湯沸かし器（AEOB）',
    accentColor: '#0f766e',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/dc52ea1f-4ff9-48dc-abf0-fad38dd21b1f_DLOQ.png',
    compatibility: {
      friendGood: { type: 'ALOB', name: '冷酷リセットボタン', description: '詰将棋が水面下で証拠を集め、リセットボタンが正面から論理で切り捨てる。どちらも冷静で感情がなく、友人というより静かな戦略家同士の同盟関係。' },
      friendBad: { type: 'ALSQ', name: '感情オフライン人間', description: '詰将棋が水面下で観察しても、オフライン人間は感情もリアクションも何もない。証拠になるものが何もなく、詰将棋は珍しく詰み手を見失う。' },
      loverGood: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が感情で爆発すると、詰将棋は冷静に証拠として記録する。ある日完璧なタイミングで「あの時のあれ」と突きつけられ、湯沸かし器は過去の爆発を後悔する。' },
      loverBad: { type: 'ALSQ', name: '感情オフライン人間', description: '詰将棋が水面下で観察しても、オフライン人間は感情もリアクションも何もない。証拠になるものが何もなく、詰将棋は詰み手を見失う。' },
    },
  },
  DLSB: {
    code: 'DLSB',
    name: '理詰め自己破壊兵器',
    tagline: '全部わかってる、でも壊れる',
    description: [
      '「私がこれだけ尽くしたのに」と、これまでの自分の貢献度や相手の非を理路整然と並べ立てた上で、自暴自棄になって自分を壊すタイプです。ただ感情で泣くのではなく、「あなたがこう言ったから、私はこう傷ついた」とロジックを立てて相手の罪を浮き彫りにしつつ、病んでいく自分を見せつけます。',
      '計算し尽くされた「かわいそうな私」を演じ切ることで、相手の良心や理性を外堀から完璧に埋め、相手自身に「自分がこの子を狂わせてしまった」と思い込ませます。',
      '頭が良く、状況を客観視できているにも関わらず、あえて自分を最悪の状況に追い込んで相手を縛り付ける、自爆テロと洗脳を掛け合わせたような高度なテクニックの持ち主です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: 'メンタル監禁官（DLSQ）',
    badMatch: 'フェードアウト幽霊（AEOQ）',
    accentColor: '#7c3aed',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/fc80d4cf-b8a4-4187-a4d0-554638a09c4f_DLSB.png',
    compatibility: {
      friendGood: { type: 'DESB', name: '涙の無限ループ', description: 'どちらも「かわいそうな私」を演じるタイプ。お互いの演技を薄々見破りながらも共感し合え、被害者同士で不思議と連帯感が生まれる。' },
      friendBad: { type: 'ALSQ', name: '感情オフライン人間', description: '自己破壊兵器が崩れていっても、オフライン人間は「あっそ」で完結。自己破壊兵器の精密な崩れ方が完全スルーされ、演技の意味を失う。' },
      loverGood: { type: 'DEOQ', name: '情緒ブラックボックス', description: 'ブラックボックスの無言の圧に、自己破壊兵器が「私が全部悪いんだ」と理詰めで崩れていく。ブラックボックスは罪悪感を覚え、二人の間に歪んだ引力が生まれる。' },
      loverBad: { type: 'ALSQ', name: '感情オフライン人間', description: '自己破壊兵器が崩れていっても、オフライン人間は「あっそ」で完結。精密な崩れ方が完全スルーされ、演技の意味を失う。' },
    },
  },
  DLSQ: {
    code: 'DLSQ',
    name: 'メンタル監禁官',
    tagline: '逃げないで、何も言わずに縛るから',
    description: [
      '「他人に期待するから傷つくんだ」と理論武装し、相手への不満があっても決して言葉には出さず、自分の中で勝手に我慢の限界を迎えるタイプです。怒りや悲しみを外に出さない代わりに、相手のSNSを無言で監視したり、行動を細かくチェックすることで精神的な繋がりを保とうとします。',
      '「私は何も言わずに耐えている」というスタンスを崩さないため、相手からすると「何を考えているかわからないが、常に監視されているような息苦しさ」を感じます。',
      '直接的な束縛はしないのに、その重すぎる無言の圧力が相手の自由を奪い、気づけば相手を精神的な牢獄に閉じ込めている、サイレントで重厚なメンタル監禁官です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '理詰め自己破壊兵器（DLSB）',
    badMatch: '瞬間湯沸かし器（AEOB）',
    accentColor: '#1d4ed8',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/89f056c1-1fcc-431e-bc4b-d7a4fafcff98_DLSQ.png',
    compatibility: {
      friendGood: { type: 'AESQ', name: 'ひとりで沈む人', description: '監禁官の重い無言の圧がひとりで沈む人の「そっとしておいてほしい」ニーズと奇妙に噛み合う。深くはないが、安定した関係。' },
      friendBad: { type: 'AEOB', name: '瞬間湯沸かし器', description: '湯沸かし器が爆発すると、監禁官は無言の重い圧で包み込む。湯沸かし器は「なんか息苦しい」と感じながらも、その圧に押さえつけられて爆発のタイミングを見失う。' },
      loverGood: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が「返事して！」と騒ぐほど、監禁官は無言の重い圧をかけてくる。核弾頭は息苦しさを感じながらも離れられず、監禁官は満足して見守る共依存の完成形。' },
      loverBad: { type: 'ALOQ', name: '完全シャットダウン', description: '監禁官が圧をかけている間にシャットダウンが全ブロック。監禁官は「逃げられた」と悟るが、何も言わない。無言同士の冷たい終わり。' },
    },
  },
  AEOB: {
    code: 'AEOB',
    name: '瞬間湯沸かし器',
    tagline: '無理。今ムカついたから終わり',
    description: [
      '地雷を踏まれた瞬間に怒りゲージがMAXになり、大声でキレたり暴れたりした挙句、その勢いのまま「もう無理、別れる！」と全てを投げ出すハリケーンのようなタイプです。根底には「自分が深く傷つく前に逃げたい」という回避心理があるため、関係を修復するための話し合いは拒否し、相手をボコボコに攻撃した後は自分から関係をクラッシュさせて消え去ります。',
      '後になって「言いすぎた」と冷静になることもありますが、その時にはすでに相手をブロックし、取り返しのつかない焼け野原にした後。',
      '怒りの瞬発力と関係破壊のスピードは16タイプの中で圧倒的ナンバーワンを誇ります。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '情緒クラッシュ逃亡者（AESB）',
    badMatch: 'メンタル監禁官（DLSQ）',
    accentColor: '#dc2626',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/56cbe354-f16f-4856-a44e-27821f3eddae_AEOB.png',
    compatibility: {
      friendGood: { type: 'AEOB', name: '瞬間湯沸かし器', description: 'どちらも瞬発的に爆発するタイプで、ちょっとしたことで大炎上する。喧嘩は激しいが仲直りも早く、嵐のような友情が長続きすることも。' },
      friendBad: { type: 'ALOQ', name: '完全シャットダウン', description: '湯沸かし器が爆発するたびにシャットダウンが減点し、ある日突然全ブロック。湯沸かし器はなぜ消えたか理解できず、また爆発する。' },
      loverGood: { type: 'DLOQ', name: '静かなる詰将棋', description: '湯沸かし器が感情で爆発すると、詰将棋は冷静に証拠として記録する。ある日完璧なタイミングで「あの時のあれ」と突きつけられ、湯沸かし器は過去の爆発を後悔する。刺激的で目が離せない関係。' },
      loverBad: { type: 'ALOQ', name: '完全シャットダウン', description: '湯沸かし器が爆発するたびにシャットダウンが減点し、ある日突然全ブロック。湯沸かし器はなぜ消えたか理解できず、また爆発する。' },
    },
  },
  AEOQ: {
    code: 'AEOQ',
    name: 'フェードアウト幽霊',
    tagline: '嫌いになった瞬間、もういない',
    description: [
      '相手の嫌な部分を見たり、機嫌を損ねた瞬間に「あ、もういいや」と感情がマイナスに振り切れ、一切の反論や話し合いの余地を与えずに完全シャットアウトする氷の処刑人です。昨日までラブラブだったとしても、自分のテリトリーを侵されたり「面倒くさい」と感じた瞬間に心のシャッターが降り、即座にブロックして姿を消します。',
      '怒りをぶつけるエネルギーすらもったいないと感じており、相手の存在そのものを「なかったこと」にするのが最も手っ取り早い解決策だと考えています。',
      '残された相手は理由もわからず、突然幽霊のように消えたあなたに一生のトラウマを抱えることになります。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '完全シャットダウン（ALOQ）',
    badMatch: '涙の無限ループ（DESB）',
    accentColor: '#475569',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/2b05416a-ce0f-4c1e-868e-73fa058e0479_AEOQ.png',
    compatibility: {
      friendGood: { type: 'AEOQ', name: 'フェードアウト幽霊', description: 'どちらも静かにフェードアウトするタイプ。ある日気づいたら自然消滅しており、どちらも傷つかない綺麗な終わり方。' },
      friendBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が「返事して！かまって！」と爆発するたびに、幽霊は「めんどくさい」と静かに距離を置く。核弾頭は幽霊の冷たさに傷ついて爆発し、幽霊はある日突然フェードアウトする。' },
      loverGood: { type: 'AESQ', name: 'ひとりで沈む人', description: 'どちらも干渉しない静かなタイプ。自然に距離を保ちながら、ゆるく続く「薄い恋愛」が成立しやすい。' },
      loverBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が「私を見て！」と爆発するたびに、幽霊は「めんどくさい」と静かに距離を置く。核弾頭の愛情の重さに幽霊は窒息し、ある日突然消える最悪のパターン。' },
    },
  },
  AESB: {
    code: 'AESB',
    name: '情緒クラッシュ逃亡者',
    tagline: 'もう無理って言いながら泣いて去る',
    description: [
      '自分が傷つくことや、相手と深く向き合うことを極端に恐れるあまり、トラブルが起きると勝手にパニックになって大号泣し、そのまま関係から逃げ出してしまうタイプです。「私が全部悪いんでしょ！」「もう関わらないで！」とヒステリックに自虐しながら泣き叫びますが、それは相手に同情してほしいからではなく、単にその場から逃げ出すための防衛本能です。',
      '感情のコントロールが効かなくなり、自爆して散らかすだけ散らかした挙句、最後は「これ以上傷つきたくない」という一心ですべてを放棄します。',
      '相手からすると、急に泣き出して勝手に消えていく、まるで情緒の通り魔のような存在です。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '瞬間湯沸かし器（AEOB）',
    badMatch: '静かなる詰将棋（DLOQ）',
    accentColor: '#be185d',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/3c03dc49-3f87-4368-9ca3-fdd9ebd8fd4f_AESB.png',
    compatibility: {
      friendGood: { type: 'DESB', name: '涙の無限ループ', description: 'どちらも感情が爆発しやすく、一緒にいると常に何かが起きている。逃亡者が泣いて逃げると涙ループが「待って！」と追いかけ、情緒の鬼ごっこが始まる。' },
      friendBad: { type: 'ALSQ', name: '感情オフライン人間', description: '逃亡者がどれだけ泣いて騒いでも、オフライン人間は「あっそ」で終了。逃亡者の感情爆発が完全スルーされ、友情が成立しない。' },
      loverGood: { type: 'DLSB', name: '理詰め自己破壊兵器', description: '自己破壊兵器が崩れると、逃亡者がパニックになって泣きながら寄り添おうとする。どちらも壊れやすいが、壊れ方が共鳴して不思議な引力が生まれる。' },
      loverBad: { type: 'AESB', name: '情緒クラッシュ逃亡者', description: 'どちらも泣きながら逃げるタイプ。トラブルが起きると同時に大号泣して同時に逃亡し、誰も残らないカオスになる。' },
    },
  },
  AESQ: {
    code: 'AESQ',
    name: 'ひとりで沈む人',
    tagline: '誰にも言わずに勝手に限界',
    description: [
      '少しでも嫌な予感がしたり、人間関係が「めんどくさい事」になりそうな気配を察知すると、ダメージを負う前にすべての連絡を絶ち、自室や深海に逃げ込む完全回避型の地雷です。相手を責めることもなく、感情を爆発させることもなく、「あ、これ以上踏み込んだら私がしんどくなるな」と判断した瞬間に、そっと電源を落とすように姿を消します。',
      '傷つくことを極度に恐れているため、誰かと深く関わる前に自ら関係を断ち切ることで自分を守っています。',
      '周囲からは「大人しい人」「何を考えているかわからない人」と思われがちですが、その内側には極度の自己防衛プログラムが走っています。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: 'フェードアウト幽霊（AEOQ）',
    badMatch: 'メンヘラ核弾頭（DEOB）',
    accentColor: '#64748b',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/be00644f-3d52-48b7-ac08-87add93c4eb4_AESQ.png',
    compatibility: {
      friendGood: { type: 'ALSQ', name: '感情オフライン人間', description: 'どちらも感情をほぼ出さず、干渉しない静かなコンビ。お互いの「そっとしておいてほしい」ニーズが完璧に一致し、最も快適な友人関係が成立する。' },
      friendBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が爆発しても、ひとりで沈む人は静かに深海に潜る。核弾頭は「なんで逃げるの！」と傷つくが、ひとりで沈む人には近づかれること自体が苦痛。' },
      loverGood: { type: 'DLSQ', name: 'メンタル監禁官', description: '監禁官の重い無言の圧がひとりで沈む人の「そっとしておいてほしい」ニーズと奇妙に噛み合う。息苦しいながらも、二人の間に独特の均衡が生まれる。' },
      loverBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭の重い愛情がひとりで沈む人を窒息させ、静かに終わる関係。核弾頭は「なんで逃げるの！」と傷つくが、ひとりで沈む人には近づかれること自体が苦痛。' },
    },
  },
  ALOB: {
    code: 'ALOB',
    name: '冷酷リセットボタン',
    tagline: '合理的にいらないから切るね',
    description: [
      '相手のダメな部分や論理的破綻を完璧に見抜き、冷酷な正論でボコボコに叩きのめしてから、即座に関係をリセットする完璧主義の裁判長です。感情でキレることはなく、「あなたのこういう行動が論理的に間違っている」「これ以上付き合うメリットがない」と、相手の言い訳を一切許さない理詰めでの制裁を下します。',
      '相手を完全に論破し、ぐうの音も出なくさせた上で「じゃあ、さようなら」と見捨てるため、相手には強烈な敗北感と絶望が残ります。',
      '情に流されることがなく、人間関係を損得や合理性でバッサリ切り捨てるサイコパス的な冷酷さを持ち合わせています。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '完全シャットダウン（ALOQ）',
    badMatch: '涙の無限ループ（DESB）',
    accentColor: '#0f172a',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/d54a214e-a0da-4b8f-ad34-8e8e46f6b6b9_ALOB.png',
    compatibility: {
      friendGood: { type: 'DLOQ', name: '静かなる詰将棋', description: '詰将棋が水面下で証拠を集め、リセットボタンが正面から論理で切り捨てる。どちらも冷静で感情がなく、友人というより静かな戦略家同士の同盟関係。' },
      friendBad: { type: 'DESB', name: '涙の無限ループ', description: '涙ループが泣き崩れると、リセットボタンが「その涙は感情操作だ」と冷静に論破する。涙ループは最強の武器を無効化され、完全に崩れ落ちる。' },
      loverGood: { type: 'DLOQ', name: '静かなる詰将棋', description: '詰将棋が水面下で証拠を集め、リセットボタンが正面から論理で切り捨てる。感情のないもの同士の静かな闘争で、奇妙な均衡が保たれる関係。' },
      loverBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が感情で爆発すると、リセットボタンが冷酷な正論で叩きのめす。核弾頭はさらに傷ついて爆発し、リセットボタンは「合理的に無理」と切り捨てる最悪の組み合わせ。' },
    },
  },
  ALOQ: {
    code: 'ALOQ',
    name: '完全シャットダウン',
    tagline: '存在ごと消すのが一番効くでしょ？',
    description: [
      '脳内で勝手に相手を減点方式で採点し、自分の定めたボーダーラインを下回って「0点」になった瞬間に、無言で関係を永久凍結するサイレントキラーです。相手に不満があっても直接文句を言わず、「あ、今のでマイナス10点」と頭の中で冷徹に計算を続けています。',
      'そして点数が尽きた日、一切の警告なしにLINEもSNSも全ブロックし、物理的にも精神的にも完全にシャットダウンします。怒りや執着といったノイズが一切なく、「価値がないと判断したから捨てる」というマシーンのような無慈悲さ。',
      '相手からすれば、地雷を踏んだ自覚すらなく突然足元が爆発したような衝撃を受けます。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '冷酷リセットボタン（ALOB）',
    badMatch: 'メンヘラ核弾頭（DEOB）',
    accentColor: '#1e293b',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/1db4fb0b-cd84-4e7f-99fd-cdff46eb7c10_ALOQ.png',
    compatibility: {
      friendGood: { type: 'ALOQ', name: '完全シャットダウン', description: 'どちらも無言で全ブロックするタイプ。お互い同時にブロックし合い、誰も傷つかない完璧な相互消滅。' },
      friendBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が騒ぐたびにシャットダウンが減点し、ある日突然全ブロック。核弾頭はなぜ消えたか理解できず、混乱と傷心だけが残る最悪の組み合わせ。' },
      loverGood: { type: 'DLOQ', name: '静かなる詰将棋', description: '詰将棋が制裁を準備している間に、シャットダウンが先に全ブロック。どちらも「先を越された」と内心悔しがる、冷たい緊張感が続く関係。' },
      loverBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭が騒ぐたびにシャットダウンが減点し、ある日突然全ブロック。核弾頭はなぜ消えたか理解できず、混乱と傷心だけが残る。' },
    },
  },
  ALSB: {
    code: 'ALSB',
    name: '自己完結型バグ',
    tagline: '自分で壊れて自分で離脱する',
    description: [
      '「これ以上一緒にいてもお互いのためにならない」「私と一緒にいるとあなたが不幸になる」と、尤もらしい理屈をこねて自分を納得させ、勝手に関係を爆破して去っていくタイプです。相手への不満や関係のほころびが見えた時、話し合って解決するのではなく、「自分が悪者になって身を引く」という美しい（しかし独りよがりな）ストーリーを頭の中で組み上げます。',
      'そして、わざと相手が嫌がることを言ったり、過剰に自分を卑下したりして派手に自爆し、関係を終わらせる理由を無理やり作り出します。',
      '論理的に逃げる口実を作り出し、悲劇の主人公として綺麗に離脱する高度なバグです。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: 'ひとりで沈む人（AESQ）',
    badMatch: 'メンヘラ核弾頭（DEOB）',
    accentColor: '#6d28d9',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/96a387e5-3d56-4ec3-a844-21c3445c09ef_ALSB.png',
    compatibility: {
      friendGood: { type: 'DESB', name: '涙の無限ループ', description: '涙ループが泣くと、バグが「私と一緒にいるとあなたが不幸になる」と自爆して去る。どちらも壊れ方が派手で、奇妙な共鳴が生まれることがある。' },
      friendBad: { type: 'ALSB', name: '自己完結型バグ', description: 'どちらも「自分が去れば解決する」と自爆して消えるタイプ。同時に自爆して同時に消え、誰も残らないコントのような終わり方をする。' },
      loverGood: { type: 'DESB', name: '涙の無限ループ', description: '涙ループが泣き崩れると、バグが「私がいなくなればいい」と自爆しようとする。どちらも自分を犠牲にする方向に動き、歪んだ献身が交差する関係。' },
      loverBad: { type: 'ALSQ', name: '感情オフライン人間', description: 'バグが自爆して消えようとすると、オフライン人間は「あっそ」で見送る。バグは「引き止めてくれない」と傷つくが、オフライン人間には何も起きていない。' },
    },
  },
  ALSQ: {
    code: 'ALSQ',
    name: '感情オフライン人間',
    tagline: '何も感じてないから、何も起きない',
    description: [
      '「そもそも他人に期待しても無駄」「深く関われば傷つくだけ」と悟りを開いており、人間関係において常に感情のスイッチをオフにしている究極の回避型地雷です。相手が浮気をしようが、理不尽にキレてこようが、感情を波立たせることなく「あっそ」で終了。',
      '怒ることも病むこともなく、ただ静かに相手との心の距離を無限に離していきます。誰に対しても一定の壁があり、どれだけ相手が熱烈にアプローチしても暖簾に腕押し。',
      '何かが起きた時は、ただ無言でスッとフェードアウトし、誰の記憶にも残らずに綺麗に消え去ります。',
    ],
    strengths: [],
    weaknesses: [],
    goodMatch: '完全シャットダウン（ALOQ）',
    badMatch: '情緒ブラックボックス（DEOQ）',
    accentColor: '#334155',
    imageUrl: 'https://storage.readdy-site.link/project_files/2c28dadf-5e81-4f61-8d94-6035bbe4ad32/ad2f3ea1-bc87-4884-a07e-ebd4b203ad07_ALSQ.png',
    compatibility: {
      friendGood: { type: 'AESQ', name: 'ひとりで沈む人', description: 'どちらも感情をほぼ出さず、干渉しない静かなコンビ。お互いの「そっとしておいてほしい」ニーズが完璧に一致し、最も快適な友人関係が成立する。' },
      friendBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭がどれだけ爆発しても、オフライン人間は「あっそ」で終了。核弾頭は反応のなさに絶望し、オフライン人間は静かにフェードアウトする。' },
      loverGood: { type: 'AESQ', name: 'ひとりで沈む人', description: 'どちらも感情をほぼ出さず、干渉しない静かなカップル。愛情確認はされないが、お互いに「そっとしておいてほしい」ニーズが一致する。' },
      loverBad: { type: 'DEOB', name: 'メンヘラ核弾頭', description: '核弾頭がどれだけ爆発しても、オフライン人間は「あっそ」で終了。核弾頭の重すぎる愛情が完全スルーされ、愛情の注ぎ先を完全に失う。' },
    },
  },
};
